import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DependencyNetwork.module.scss';
import { DependencyEdgeDto, DependencyGraphDto, DependencyNodeDto } from '../../../api/model';
import { chrome, series, status } from '../reportTheme';

interface DependencyNetworkProps {
  graph?: DependencyGraphDto;
  /** Translated phase names; the graph carries the stored name as a fallback. */
  phaseNames: Map<number, string>;
}

const COLUMN_WIDTH = 190;
const ROW_HEIGHT = 34;
const NODE_RADIUS = 6;
const TOP_PADDING = 28;
const LABEL_OFFSET = 12;
const MAX_LABEL = 22;
/** How far an edge between two nodes of the same column bulges sideways. */
const SAME_COLUMN_BOW = 18;

const edgeColor = (edge: DependencyEdgeDto): string => {
  if (edge.violated) return status.critical;
  if (edge.inCycle) return status.warning;
  if (!edge.scheduleEffective) return chrome.axis;
  return series.blue;
};

/**
 * The dependency structure as a small network diagram (spec §17).
 *
 * Laid out deterministically rather than force-directed - one column per
 * phase, in phase order - so the picture is stable between renders and needs
 * no graph-layout dependency. Only deliverables that actually have a
 * dependency are drawn: a project's deliverables run into the dozens and the
 * isolated ones say nothing about the dependency structure.
 */
const DependencyNetwork: React.FC<DependencyNetworkProps> = ({ graph, phaseNames }) => {
  const { t } = useTranslation('templates');

  const layout = useMemo(() => {
    const edges = (graph?.edges ?? []).filter((e) => e.from != null && e.to != null);
    const connected = new Set<number>();
    for (const edge of edges) {
      connected.add(edge.from as number);
      connected.add(edge.to as number);
    }

    const nodes = (graph?.nodes ?? []).filter((n) => n.id != null && connected.has(n.id));
    if (nodes.length === 0) return null;

    // group into columns by phase, keeping the phase order the project has
    const byPhase = new Map<number, DependencyNodeDto[]>();
    for (const node of nodes) {
      const phaseId = node.phaseId ?? -1;
      const column = byPhase.get(phaseId);
      if (column) column.push(node);
      else byPhase.set(phaseId, [node]);
    }

    const columns = [...byPhase.entries()].sort((a, b) => a[0] - b[0]);
    const positions = new Map<number, { x: number; y: number }>();
    columns.forEach(([, columnNodes], columnIndex) => {
      columnNodes.forEach((node, rowIndex) => {
        positions.set(node.id as number, {
          x: columnIndex * COLUMN_WIDTH + 24 + SAME_COLUMN_BOW,
          y: TOP_PADDING + rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2,
        });
      });
    });

    const tallest = Math.max(...columns.map(([, c]) => c.length));
    return {
      columns,
      positions,
      edges,
      width: columns.length * COLUMN_WIDTH + 24 + SAME_COLUMN_BOW,
      height: TOP_PADDING + tallest * ROW_HEIGHT + 16,
    };
  }, [graph]);

  if (!layout) {
    return <div className={styles.empty}>{t('report.dependencyNetwork.empty')}</div>;
  }

  const shorten = (label?: string) =>
    !label ? '' : label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;

  return (
    <div className={styles.wrapper}>
      <div className={styles.scroll}>
        <svg width={layout.width} height={layout.height} role="img">
          {layout.columns.map(([phaseId], columnIndex) => (
            <text
              key={`col-${phaseId}`}
              x={columnIndex * COLUMN_WIDTH + 24}
              y={14}
              className={styles.columnLabel}
            >
              {shorten(phaseNames.get(phaseId) ?? t('report.dependencyNetwork.noPhase'))}
            </text>
          ))}

          {layout.edges.map((edge) => {
            const from = layout.positions.get(edge.from as number);
            const to = layout.positions.get(edge.to as number);
            if (!from || !to) return null;
            // Bow the curve out. Within one column the two ends share an x, so
            // a midpoint control point would draw a straight line through every
            // node between them; bulging sideways keeps the edge readable.
            const sameColumn = from.x === to.x;
            const controlX = sameColumn
              ? from.x - SAME_COLUMN_BOW
              : (from.x + to.x) / 2;
            return (
              <path
                key={`edge-${edge.id}`}
                d={`M ${from.x} ${from.y} C ${controlX} ${from.y}, ${controlX} ${to.y}, ${to.x} ${to.y}`}
                fill="none"
                stroke={edgeColor(edge)}
                strokeWidth={edge.scheduleEffective ? 1.6 : 1.2}
                strokeDasharray={edge.scheduleEffective ? undefined : '4 3'}
                opacity={0.9}
              />
            );
          })}

          {layout.columns.flatMap(([, columnNodes]) =>
            columnNodes.map((node) => {
              const position = layout.positions.get(node.id as number);
              if (!position) return null;
              return (
                <g key={`node-${node.id}`}>
                  <circle
                    cx={position.x}
                    cy={position.y}
                    r={NODE_RADIUS}
                    fill={chrome.surface}
                    stroke={series.blue}
                    strokeWidth={2}
                  />
                  <title>{node.label}</title>
                  <text
                    x={position.x + LABEL_OFFSET}
                    y={position.y + 4}
                    className={styles.nodeLabel}
                  >
                    {shorten(node.label)}
                  </text>
                </g>
              );
            }),
          )}
        </svg>
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendLine} style={{ borderTopColor: series.blue }} />
          {t('report.dependencyNetwork.legend.scheduleEffective')}
        </span>
        <span className={styles.legendItem}>
          <span
            className={styles.legendLine}
            style={{ borderTopColor: chrome.axis, borderTopStyle: 'dashed' }}
          />
          {t('report.dependencyNetwork.legend.informational')}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendLine} style={{ borderTopColor: status.critical }} />
          {t('report.dependencyNetwork.legend.violated')}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendLine} style={{ borderTopColor: status.warning }} />
          {t('report.dependencyNetwork.legend.inCycle')}
        </span>
      </div>
    </div>
  );
};

export default DependencyNetwork;
